import React from 'react';
import {
  Alert, Box, FormControl, IconButton, InputLabel, MenuItem, Select, Tooltip,
  Typography,
} from '@mui/material';
import { LayersOutlined, TvOutlined } from '@mui/icons-material';
import * as ETVConstants from '../ETVConstants';
import FloorDeviceConnection from './FloorDeviceConnection';
import ETVActionButton from '../tools/ETVActionButton';
import ETVPage from '../tools/ETVPage';
import ETVPopup from '../tools/ETVPopup';
import ETVTable from '../tools/ETVTable';

class DeviceFloorMgmt extends React.Component {
  state = {
    locations: [],
    locationsLoaded: false,
    selected_location_id: '',
    devices: [],
    devicesLoaded: false,
    isLoadingDevices: false,
    window: '',
    selected_device_id: '',
    error: null,
  };

  componentDidMount() {
    this.loadLocations();
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      this.state.selected_location_id
      && prevState.selected_location_id !== this.state.selected_location_id
    ) {
      this.loadDevices();
    }
  }

  loadLocations = () => {
    const url = `${ETVConstants.getServerBase()}/floor_access?action=get_locations&user_id=${this.props.user_id}`;
    fetch(url)
      .then(res => res.json())
      .then(
        (result) => {
          const list = result.list || [];
          const onlyOne = list.length === 1 ? String(list[0].location_id) : '';
          this.setState({
            locations: list,
            locationsLoaded: true,
            selected_location_id: onlyOne,
            error: null,
          });
        },
        (error) => this.setState({ locations: [], locationsLoaded: false, error })
      );
  };

  loadDevices = () => {
    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');
    const url = `${ETVConstants.getServerBase()}/device_mgmt?action=list&owner_id=${this.props.user_id}&location_id=${this.state.selected_location_id}`;

    this.setState({ isLoadingDevices: true, devicesLoaded: false, error: null });
    fetch(url, { headers: { user_id: reqUserID, access_token: accessToken } })
      .then(res => res.json())
      .then(
        (result) => {
          const sorted = (result.list || []).slice().sort(ETVConstants.compareDevice);
          this.setState({ devices: sorted, devicesLoaded: true, isLoadingDevices: false });
        },
        (error) => this.setState({ devices: [], devicesLoaded: false, isLoadingDevices: false, error })
      );
  };

  showWindow = (id, show) => this.setState({ window: show ? id : '' });

  setFloorConnection = (deviceID) => {
    this.setState({ selected_device_id: deviceID, window: 'device_floor_div' });
  };

  columnsDevices = () => ([
    { title: ETVConstants.trans('name'), field: 'device_name' },
    {
      title: ETVConstants.trans('serving_floors'),
      field: '',
      width: 120,
      cellStyle: { width: 120, textAlign: 'center' },
      headerStyle: { width: 120, textAlign: 'center' },
      render: rowData => (
        <Tooltip title={ETVConstants.trans('set_floors')}>
          <IconButton color="primary" size="small" onClick={() => this.setFloorConnection(rowData.id)}>
            <LayersOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ]);

  render() {
    const {
      locations, locationsLoaded, selected_location_id,
      devices, devicesLoaded, isLoadingDevices, error,
    } = this.state;

    return (
      <ETVPage
        width="small"
        icon={<TvOutlined fontSize="large" />}
        title={ETVConstants.trans('device_floor_management') || 'Device–Floor Mapping'}
        description="Select a building to see and manage its device-to-floor mapping."
        loading={isLoadingDevices}
      >
        <Box sx={{ width: '100%' }}>
          {locations.length === 1 ? (
            <Box sx={{ mb: 3 }}>
              <Typography variant="caption" color="text.secondary">Building</Typography>
              <Typography variant="body1" fontWeight={600}>
                {locations[0].name} - {locations[0].address}
              </Typography>
            </Box>
          ) : (
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="device-floor-location-label">Building</InputLabel>
              <Select
                labelId="device-floor-location-label"
                value={selected_location_id}
                label="Building"
                disabled={!locationsLoaded}
                onChange={(event) => this.setState({
                  selected_location_id: String(event.target.value),
                  devicesLoaded: false,
                  devices: [],
                })}
              >
                {locations.map(location => (
                  <MenuItem key={location.location_id} value={String(location.location_id)}>
                    {location.name} - {location.address}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {ETVConstants.trans('error') || 'Error'}: {String(error)}
            </Alert>
          )}

          {!selected_location_id ? null : devicesLoaded && devices.length === 0 ? (
            <Alert severity="warning">
              {ETVConstants.trans('no_devices_found') || 'No devices found for this location.'}
            </Alert>
          ) : (
            <ETVTable
              data={devices}
              columns={this.columnsDevices()}
              title=""
              pageSize={10}
              search
              embedded
              options={{ padding: 'dense', tableLayout: 'fixed' }}
            />
          )}
        </Box>

        <ETVPopup
          open={this.state.window === 'device_floor_div'}
          width="small"
          onClose={() => this.showWindow('', false)}
          icon={<LayersOutlined />}
          title={ETVConstants.trans('serving_floors') || 'Serving floors'}
          description={ETVConstants.trans('device_floor_mapping_description') || 'Select the floors served by this screen.'}
          bottomActions={(
            <ETVActionButton
              action="cancel"
              label={ETVConstants.trans('close') || 'Close'}
              onClick={() => this.showWindow('', false)}
            />
          )}
        >
          {this.state.selected_device_id && selected_location_id ? (
            <FloorDeviceConnection
              device_id={this.state.selected_device_id}
              location_id={selected_location_id}
            />
          ) : null}
        </ETVPopup>
      </ETVPage>
    );
  }
}

export default DeviceFloorMgmt;
